import { Component, OnInit, ChangeDetectionStrategy, inject, AfterViewInit, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { ShowroomService } from '@app/our-showroom/services/showroom.service';
import { CategoryProductsService } from '@shared/service';
import { WindowService } from '@shared/window';
import { Autoplay, FreeMode, Pagination } from 'swiper/modules';
import { combineLatest, delay, filter, Subscription, take, tap } from 'rxjs';
import Swiper from 'swiper';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { RouterLink } from '@angular/router';
import { ProductThumbnailComponent } from '../../../shared/components/product-thumbnail/product-thumbnail.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { LocalizePipe } from '../../../i18n/pipes/lozalize.pipe';

@Component({
    selector: 'app-our-products',
    templateUrl: './our-products.component.html',
    styleUrls: ['./our-products.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ShowroomService],
    imports: [ButtonComponent, RouterLink, ProductThumbnailComponent, AsyncPipe, TranslatePipe, LocalizePipe],
})
export class OurProductsComponent implements OnInit, AfterViewInit, OnDestroy {
    private zone = inject(NgZone);
    private categoryProductsService = inject(CategoryProductsService);
    private windowService = inject(WindowService);

    @ViewChild('swiper') swiperRef!: ElementRef<HTMLDivElement>;
    @ViewChild('swiperPagination') swiperPaginationRef!: ElementRef<HTMLDivElement>;

    readonly isClient = this.windowService.isClient;

    private swiper?: Swiper;

    products$ = this.categoryProductsService.productsByCategory$;
    category$ = this.categoryProductsService.category$;

    private subscriptions$ = new Subscription();

    ngOnInit(): void {
        this.categoryProductsService.dispatch();
    }

    ngAfterViewInit() {
        if (this.windowService.isBrowser) {
            this.subscriptions$.add(
                combineLatest([this.products$.pipe(filter((value) => value !== null)), this.category$.pipe(filter((value) => value !== null))])
                    .pipe(
                        take(1),
                        delay(0),
                        tap(() => this.initSwiper()),
                    )
                    .subscribe(),
            );
        }
    }

    ngOnDestroy(): void {
        this.swiper?.destroy();
        this.subscriptions$.unsubscribe();
    }

    private initSwiper() {
        this.zone.runOutsideAngular(() => {
            this.swiper = new Swiper(this.swiperRef.nativeElement, {
                modules: [Pagination, Autoplay, FreeMode],
                spaceBetween: 26,
                autoplay: {
                    delay: 4000,
                    disableOnInteraction: false,
                },
                freeMode: {
                    enabled: true,
                    sticky: true,
                    momentum: false,
                },
                speed: 1500,
                loop: true,
                shortSwipes: false,
                pagination: {
                    el: this.swiperPaginationRef.nativeElement,
                    type: 'bullets',
                },
                watchOverflow: true,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                breakpoints: {
                    0: {
                        slidesPerView: 'auto',
                    },
                    320: {
                        slidesPerView: 1.5,
                    },
                    576: {
                        slidesPerView: 2,
                    },
                    768: {
                        slidesPerView: 3,
                    },
                },
            });
        });
    }
}
